import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { TeamAccessTable } from "../components/settings/TeamAccessTable";
import SignaturesAdmin from "../components/settings/SignaturesAdmin";
import LeadClaimSettings from "../components/settings/LeadClaimSettings";
import DataExportSettings from "../components/settings/DataExportSettings";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Plus, Trash, ShieldCheck, User, Lock, PencilSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { Checkbox } from "../components/ui/checkbox";
import { PERMISSION_MODULES, DEFAULT_PERMISSIONS, ACCOUNT_TYPES } from "../lib/permissions";
import { toast } from "sonner";

export default function Settings() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [salespeople, setSalespeople] = useState([]);
  const [chauffeurs, setChauffeurs] = useState([]);
  const [carOwners, setCarOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "staff", salesperson: "none", chauffeur_id: "none", investor_owner_id: "none", permissions: DEFAULT_PERMISSIONS });
  const [saving, setSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [accessUser, setAccessUser] = useState(null);
  const [accessForm, setAccessForm] = useState(null);
  const [accessPerms, setAccessPerms] = useState([]);
  const [accessRole, setAccessRole] = useState("staff");
  const [accessSaving, setAccessSaving] = useState(false);
  const [pwReset, setPwReset] = useState({ password: "" });
  // SALES-015: admin rows get a password-only path of their own. It calls the
  // reset route and NOTHING else — the operator PUT still refuses an admin
  // target by design, so touching it here would 400 before the reset ran.
  const [resetUser, setResetUser] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [resetSaving, setResetSaving] = useState(false);
  // SALES-025: end every session on one account. The confirm, then an offer to
  // also set a temp password that reuses SALES-015's reset dialog above.
  const [signoutUser, setSignoutUser] = useState(null);
  const [signoutSaving, setSignoutSaving] = useState(false);
  const [offerPwUser, setOfferPwUser] = useState(null);
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [ownerEditing, setOwnerEditing] = useState(null);
  const [ownerForm, setOwnerForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [ownerSaving, setOwnerSaving] = useState(false);

  const togglePerm = (list, key) => (list.includes(key) ? list.filter((p) => p !== key) : [...list, key]);

  // SALES-026: patch a single account in place (setUsers is stable, so these
  // callbacks are stable across renders); refetch just the users list when a
  // cross-row rule is owned by the server and we will not mirror it here.
  const patchUser = useCallback((id, patch) => {
    setUsers((list) => list.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  }, []);
  const refetchUsers = useCallback(async () => {
    try { const { data } = await api.get("/auth/users"); setUsers(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const openAccess = useCallback((u) => {
    setAccessUser(u);
    setAccessForm({
      name: u.name || "",
      email: u.email || "",
      salesperson: u.salesperson || "none",
      chauffeur_id: u.chauffeur_id || "none",
      investor_owner_id: u.investor_owner_id || "none",
    });
    setAccessPerms(Array.isArray(u.permissions) ? u.permissions : DEFAULT_PERMISSIONS);
    setAccessRole(u.role || "staff");
    setPwReset({ password: "" });
  }, []);

  const openReset = useCallback((u) => { setResetUser(u); setResetPw(""); }, []);

  const submitReset = async () => {
    if (resetPw.length < 6) { toast.error("Temporary password must be at least 6 characters"); return; }
    setResetSaving(true);
    try {
      const id = resetUser.id;
      await api.post(`/auth/users/${id}/reset-password`, { new_password: resetPw });
      toast.success("Temporary password set — they will be asked to choose their own at next sign-in");
      setResetUser(null);
      setResetPw("");
      patchUser(id, { must_change_password: true });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResetSaving(false); }
  };

  const doSignOut = async () => {
    setSignoutSaving(true);
    try {
      await api.post(`/auth/users/${signoutUser.id}/sign-out-everywhere`);
      const u = signoutUser;
      toast.success(`${u.name || u.email} signed out on every device`);
      setSignoutUser(null);
      setOfferPwUser(u); // offer to also set a temp password
      // SALES-026: nothing on the row changes, so no refetch.
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSignoutSaving(false); }
  };

  const saveAccess = async () => {
    if (!accessForm.name.trim()) { toast.error("Name is required"); return; }
    if (!accessForm.email.trim()) { toast.error("Email is required"); return; }
    if (pwReset.password && pwReset.password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setAccessSaving(true);
    try {
      // Admin & investor logins are role-locked on the backend — perms are ignored.
      const perms = accessRole === "admin" || accessRole === "investor" ? [] : accessPerms;
      const editedId = accessUser.id;
      const oldSp = accessUser.salesperson || "";
      const newSp = accessForm.salesperson === "none" ? "" : accessForm.salesperson;
      const { data } = await api.put(`/auth/users/${editedId}`, {
        name: accessForm.name.trim(),
        email: accessForm.email.trim(),
        role: accessRole,
        permissions: perms,
        salesperson: newSp,
        chauffeur_id: accessForm.chauffeur_id === "none" ? "" : accessForm.chauffeur_id,
        investor_owner_id: accessForm.investor_owner_id === "none" ? "" : accessForm.investor_owner_id,
      });
      if (pwReset.password) {
        await api.post(`/auth/users/${editedId}/reset-password`, { new_password: pwReset.password });
      }
      toast.success(`${accessForm.name.trim()} updated${pwReset.password ? " · temp password set" : ""}`);
      setAccessUser(null);
      // Assigning a salesperson NAME releases whoever held it before — a rule the
      // server owns. Rather than copy it here, refetch just the users list.
      // Otherwise patch this one account in place from the response.
      if (newSp && newSp !== oldSp) {
        await refetchUsers();
      } else {
        patchUser(editedId, { ...data.user, ...(pwReset.password ? { must_change_password: true } : {}) });
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAccessSaving(false); }
  };

  const isAdmin = user?.role === "admin";

  const [staleDays, setStaleDays] = useState(60);
  const [staleSaving, setStaleSaving] = useState(false);
  const saveStaleDays = async () => {
    setStaleSaving(true);
    try {
      const { data } = await api.put("/crm-settings", { stale_lead_days: Number(staleDays) || 60 });
      setStaleDays(data.stale_lead_days);
      toast.success(`Untouched leads now auto-close after ${data.stale_lead_days} days`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setStaleSaving(false); }
  };

  const changePassword = async () => {
    if (pwForm.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pwForm.new_password !== pwForm.confirm) { toast.error("New passwords do not match"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success("Password updated");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPwSaving(false); }
  };

  const load = useCallback(async () => {
    if (!isAdmin) { setLoading(false); return; }
    setLoading(true);
    try {
      const [{ data }, sp, ch, co, cs] = await Promise.all([api.get("/auth/users"), api.get("/salespeople"), api.get("/chauffeurs"), api.get("/car-owners"), api.get("/crm-settings")]);
      setUsers(data);
      setSalespeople(sp.data || []);
      setChauffeurs(ch.data || []);
      setCarOwners(co.data || []);
      setStaleDays(cs.data?.stale_lead_days ?? 60);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const createUser = async () => {
    setSaving(true);
    try {
      await api.post("/auth/register", {
        ...form,
        salesperson: form.salesperson === "none" ? "" : form.salesperson,
        chauffeur_id: form.chauffeur_id === "none" ? "" : form.chauffeur_id,
        investor_owner_id: form.investor_owner_id === "none" ? "" : form.investor_owner_id,
        permissions: form.role === "admin" ? null : form.permissions,
      });
      toast.success("User created");
      setOpen(false);
      setForm({ email: "", password: "", name: "", role: "staff", salesperson: "none", chauffeur_id: "none", investor_owner_id: "none", permissions: DEFAULT_PERMISSIONS });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const assignInvestorOwner = useCallback(async (userId, oid) => {
    try {
      const { data } = await api.put(`/auth/users/${userId}/investor-owner`, { investor_owner_id: oid === "none" ? "" : oid });
      const label = oid === "none" ? "Owner unlinked" : `Tied to ${carOwners.find((o) => o.id === oid)?.name || "owner"}`;
      toast.success(label);
      patchUser(userId, { investor_owner_id: data.investor_owner_id });
    } catch (e) { toast.error(formatApiError(e)); }
  }, [carOwners, patchUser]);

  const openOwnerCreate = () => {
    setOwnerEditing(null);
    setOwnerForm({ name: "", email: "", phone: "", notes: "" });
    setOwnerOpen(true);
  };
  const openOwnerEdit = (o) => {
    setOwnerEditing(o);
    setOwnerForm({ name: o.name || "", email: o.email || "", phone: o.phone || "", notes: o.notes || "" });
    setOwnerOpen(true);
  };
  const saveOwner = async () => {
    if (!ownerForm.name.trim()) { toast.error("Name is required"); return; }
    setOwnerSaving(true);
    try {
      if (ownerEditing) {
        await api.put(`/car-owners/${ownerEditing.id}`, ownerForm);
        toast.success("Car owner updated");
      } else {
        await api.post(`/car-owners`, ownerForm);
        toast.success("Car owner added");
      }
      setOwnerOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOwnerSaving(false); }
  };
  const deleteOwner = async (id) => {
    if (!confirm("Remove this car owner? Any vehicles or logins tied to them will be unlinked.")) return;
    try {
      await api.delete(`/car-owners/${id}`);
      toast.success("Car owner removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const assignSalesperson = useCallback(async (userId, name) => {
    try {
      await api.put(`/auth/users/${userId}/salesperson`, { salesperson: name === "none" ? "" : name });
      toast.success(name === "none" ? "Salesperson unlinked" : `Tied to ${name}`);
      // Server releases the salesperson's previous holder (one per login) — refetch
      // just the users list so that cross-row rule stays server-owned.
      await refetchUsers();
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refetchUsers]);

  const assignChauffeur = useCallback(async (userId, cid) => {
    try {
      const { data } = await api.put(`/auth/users/${userId}/chauffeur`, { chauffeur_id: cid === "none" ? "" : cid });
      const c = chauffeurs.find((x) => x.id === cid);
      const nm = c ? `${c.first_name || ""} ${c.last_name || ""}`.trim() : "";
      toast.success(cid === "none" ? "Driver unlinked" : `Tied to ${nm || "driver"}`);
      patchUser(userId, { chauffeur_id: data.chauffeur_id });
    } catch (e) { toast.error(formatApiError(e)); }
  }, [chauffeurs, patchUser]);

  const deleteUser = useCallback(async (id) => {
    if (!confirm("Remove this user?")) return;
    try {
      await api.delete(`/auth/users/${id}`);
      toast.success("User removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }, [load]);

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div className="border border-border bg-card p-8">
        <div className="flex items-center gap-3 mb-1">
          <User size={20} className="text-primary" />
          <div className="label-mono">Your account</div>
        </div>
        <h2 className="font-heading text-2xl mt-1">{user?.name}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          <Detail label="Email" value={user?.email} />
          <Detail label="Role" value={user?.role} />
          <Detail label="ID" value={user?.id} mono />
        </div>
      </div>

      {isAdmin ? (
        <div className="border border-border bg-card p-8" data-testid="change-password-card">
        <div className="label-mono mb-1">Security</div>
        <h2 className="font-heading text-2xl mt-1">Change password</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <Field label="Current password"><PasswordInput value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} className="bg-input border-border" data-testid="current-password" /></Field>
          <Field label="New password"><PasswordInput value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} className="bg-input border-border" data-testid="new-password" /></Field>
          <Field label="Confirm new password"><PasswordInput value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} className="bg-input border-border" data-testid="confirm-password" /></Field>
        </div>
        <div className="mt-5">
          <Button onClick={changePassword} disabled={pwSaving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="change-password-button">{pwSaving ? "Saving…" : "Update password"}</Button>
        </div>
      </div>
      ) : (
        <div className="border border-border bg-card p-8" data-testid="password-readonly-card">
          <div className="label-mono mb-1">Security</div>
          <h2 className="font-heading text-2xl mt-1">Password</h2>
          <p className="text-sm text-muted-foreground mt-3">Only an admin can change account passwords. Ask an admin to issue you a new temporary one — you&apos;ll be asked to set your own the next time you sign in.</p>
        </div>
      )}

      {isAdmin ? (
        <div className="border border-border bg-card">
          <div className="px-8 py-6 border-b border-border flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-primary" /><div className="label-mono">Admin</div></div>
              <h2 className="font-heading text-2xl mt-1">Team access</h2>
            </div>
            <Button onClick={() => setOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-user-button">
              <Plus size={16} className="mr-1.5" /> Add operator
            </Button>
          </div>
          <TeamAccessTable
            users={users}
            salespeople={salespeople}
            chauffeurs={chauffeurs}
            carOwners={carOwners}
            loading={loading}
            currentUserId={user?.id}
            onOpenAccess={openAccess}
            onResetPassword={openReset}
            onSignOut={setSignoutUser}
            onDelete={deleteUser}
            onAssignSalesperson={assignSalesperson}
            onAssignChauffeur={assignChauffeur}
            onAssignOwner={assignInvestorOwner}
          />
        </div>
      ) : null}

      {isAdmin ? <SignaturesAdmin /> : null}

      <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Reset password</DialogTitle>
            <DialogDescription>
              {resetUser?.name || resetUser?.email} signs in with this temporary password and is asked to choose their own straight away. Nothing else about the login changes.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <Label className="label-mono text-xs">Temporary password</Label>
            <PasswordInput
              value={resetPw}
              onChange={(e) => setResetPw(e.target.value)}
              placeholder="At least 6 characters"
              className="bg-input border-border mt-1.5"
              data-testid="reset-password-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetUser(null)} data-testid="reset-password-cancel">Cancel</Button>
            <Button onClick={submitReset} disabled={resetSaving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="reset-password-submit">
              {resetSaving ? "Resetting…" : "Reset password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SALES-025: confirm signing an account out of every device */}
      <Dialog open={!!signoutUser} onOpenChange={(o) => !o && setSignoutUser(null)}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="signout-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Sign out everywhere</DialogTitle>
            <DialogDescription>
              Sign {signoutUser?.name || signoutUser?.email} out on every device? They can sign back in with their current password.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignoutUser(null)} data-testid="signout-confirm-cancel">Cancel</Button>
            <Button onClick={doSignOut} disabled={signoutSaving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="signout-confirm-submit">
              {signoutSaving ? "Signing out…" : "Sign out everywhere"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SALES-025: after sign-out, offer to also set a temp password (reuses the reset dialog) */}
      <Dialog open={!!offerPwUser} onOpenChange={(o) => !o && setOfferPwUser(null)}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="signout-offer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Signed out everywhere</DialogTitle>
            <DialogDescription>
              {offerPwUser?.name || offerPwUser?.email} has been signed out on every device. Also set a new temporary password?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOfferPwUser(null)} data-testid="signout-offer-later">Not now</Button>
            <Button onClick={() => { setResetUser(offerPwUser); setResetPw(""); setOfferPwUser(null); }} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="signout-offer-setpw">
              Set a temporary password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {isAdmin ? (
        <div className="border border-border bg-card" data-testid="lead-rules-card">
          <div className="px-8 py-6 border-b border-border">
            <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-primary" /><div className="label-mono">Admin</div></div>
            <h2 className="font-heading text-2xl mt-1">Lead rules</h2>
            <p className="text-xs text-muted-foreground mt-1">A new lead nobody ever replied to is closed as lost (&ldquo;No response&rdquo;) automatically. Runs nightly.</p>
          </div>
          <div className="px-8 py-6 flex flex-wrap items-end gap-3">
            <div>
              <Label className="label-mono mb-1.5 block">Auto-close after (days)</Label>
              <Input
                type="number" min={1} max={365} value={staleDays}
                onChange={(e) => setStaleDays(e.target.value)}
                className="bg-input border-border w-32" data-testid="stale-lead-days"
              />
            </div>
            <Button onClick={saveStaleDays} disabled={staleSaving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-stale-lead-days">
              {staleSaving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      ) : null}

      {isAdmin ? (
        <div className="border border-border bg-card" data-testid="booking-history-admin-card">
          <div className="px-8 py-6 border-b border-border">
            <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-primary" /><div className="label-mono">Admin</div></div>
            <h2 className="font-heading text-2xl mt-1">Booking history</h2>
            <p className="text-xs text-muted-foreground mt-1">The recycle bin for deleted bookings, and the diagnostics / link-repair tool for salesperson &amp; driver history.</p>
          </div>
          <div className="px-8 py-6 grid gap-3 sm:grid-cols-2">
            <Link to="/settings/deleted-bookings" className="group border border-border rounded-2xl p-5 hover:border-primary/50 transition-colors" data-testid="nav-deleted-bookings">
              <div className="flex items-center gap-2"><Trash size={18} className="text-primary" /><div className="font-heading text-lg">Deleted bookings</div></div>
              <p className="text-xs text-muted-foreground mt-1.5">View and restore anything that was deleted. Nothing is ever purged automatically.</p>
            </Link>
            <Link to="/settings/booking-history" className="group border border-border rounded-2xl p-5 hover:border-primary/50 transition-colors" data-testid="nav-booking-history-diagnostics">
              <div className="flex items-center gap-2"><MagnifyingGlass size={18} className="text-primary" /><div className="font-heading text-lg">Booking history diagnostics</div></div>
              <p className="text-xs text-muted-foreground mt-1.5">Why bookings drop out of personal views, plus the salesperson / driver link-repair dry run. Writes nothing until you apply.</p>
            </Link>
          </div>
        </div>
      ) : null}

      {isAdmin ? <LeadClaimSettings users={users} /> : null}

      {isAdmin ? <DataExportSettings /> : null}

      {isAdmin ? (
        <div className="border border-border bg-card" data-testid="car-owners-card">
          <div className="px-8 py-6 border-b border-border flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-primary" /><div className="label-mono">Admin</div></div>
              <h2 className="font-heading text-2xl mt-1">Car owners (Investors)</h2>
              <p className="text-xs text-muted-foreground mt-1">Manage 3rd-party owners whose cars are in your rental fleet. Each owner can be tied to a login above so they only see their own car&apos;s bookings & calendar.</p>
            </div>
            <Button onClick={openOwnerCreate} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-car-owner-button">
              <Plus size={16} className="mr-1.5" /> Add car owner
            </Button>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-6 py-3 label-mono font-normal">Name</th>
                <th className="text-left px-6 py-3 label-mono font-normal">Email</th>
                <th className="text-left px-6 py-3 label-mono font-normal">Phone</th>
                <th className="text-left px-6 py-3 label-mono font-normal">Notes</th>
                <th className="text-right px-6 py-3 label-mono font-normal w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {carOwners.length === 0 ? (
                <tr><td colSpan={5} className="p-10 text-center text-muted-foreground">No car owners yet. Click &quot;Add car owner&quot; to create one.</td></tr>
              ) : carOwners.map((o) => (
                <tr key={o.id} className="border-b border-border/40" data-testid={`car-owner-row-${o.id}`}>
                  <td className="px-6 py-3 font-heading">{o.name}</td>
                  <td className="px-6 py-3">{o.email || "—"}</td>
                  <td className="px-6 py-3">{o.phone || "—"}</td>
                  <td className="px-6 py-3 text-muted-foreground">{o.notes || "—"}</td>
                  <td className="px-6 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => openOwnerEdit(o)} data-testid={`car-owner-edit-${o.id}`}>
                      <PencilSimple size={14} className="text-primary" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteOwner(o.id)} data-testid={`car-owner-delete-${o.id}`}>
                      <Trash size={14} className="text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <Dialog open={ownerOpen} onOpenChange={setOwnerOpen}>
        <DialogContent className="bg-card border-border" data-testid="car-owner-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{ownerEditing ? "Edit car owner" : "Add car owner"}</DialogTitle>
            <DialogDescription>Investors whose cars are on your rental fleet.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Field label="Name"><Input value={ownerForm.name} onChange={(e) => setOwnerForm({ ...ownerForm, name: e.target.value })} className="bg-input border-border" data-testid="car-owner-name" /></Field>
            <Field label="Email"><Input type="email" value={ownerForm.email} onChange={(e) => setOwnerForm({ ...ownerForm, email: e.target.value })} className="bg-input border-border" data-testid="car-owner-email" /></Field>
            <Field label="Phone"><Input value={ownerForm.phone} onChange={(e) => setOwnerForm({ ...ownerForm, phone: e.target.value })} className="bg-input border-border" data-testid="car-owner-phone" /></Field>
            <Field label="Notes"><Input value={ownerForm.notes} onChange={(e) => setOwnerForm({ ...ownerForm, notes: e.target.value })} className="bg-input border-border" data-testid="car-owner-notes" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOwnerOpen(false)}>Cancel</Button>
            <Button onClick={saveOwner} disabled={ownerSaving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-car-owner">
              {ownerSaving ? "Saving…" : (ownerEditing ? "Save changes" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Add operator</DialogTitle><DialogDescription>Create a login and choose their access level.</DialogDescription></DialogHeader>
          <div className="space-y-4 mt-2">
            <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-input border-border" data-testid="new-user-name" /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-input border-border" data-testid="new-user-email" /></Field>
            <Field label="Temporary password"><PasswordInput value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-input border-border" data-testid="new-user-password" /></Field>
            <p className="text-xs text-muted-foreground -mt-2">They sign in with this once and are then asked to set their own password.</p>
            <Field label="Role">
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="bg-input border-border" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACCOUNT_TYPES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Salesperson (for My Bookings)">
              <Select value={form.salesperson} onValueChange={(v) => setForm({ ...form, salesperson: v })}>
                <SelectTrigger className="bg-input border-border" data-testid="new-user-salesperson"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Not linked —</SelectItem>
                  {salespeople.map((sp) => <SelectItem key={sp.id} value={sp.name}>{sp.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Driver (for My Schedule)">
              <Select value={form.chauffeur_id} onValueChange={(v) => setForm({ ...form, chauffeur_id: v })}>
                <SelectTrigger className="bg-input border-border" data-testid="new-user-chauffeur"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Not linked —</SelectItem>
                  {chauffeurs.map((c) => <SelectItem key={c.id} value={c.id}>{`${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || c.id}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            {form.role === "investor" && (
              <Field label="Car owner (which investor is this?)">
                <Select value={form.investor_owner_id} onValueChange={(v) => setForm({ ...form, investor_owner_id: v })}>
                  <SelectTrigger className="bg-input border-border" data-testid="new-user-investor-owner"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Not linked (won&apos;t see any cars) —</SelectItem>
                    {carOwners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
            {form.role !== "admin" && form.role !== "investor" && (
              <div data-testid="new-user-permissions">
                <Label className="label-mono mb-1.5 block">Allowances — tick what this person can access</Label>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 border border-border rounded-md p-3 max-h-56 overflow-y-auto">
                  {PERMISSION_MODULES.map((m) => (
                    <label key={m.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`new-perm-${m.key}`}>
                      <Checkbox checked={form.permissions.includes(m.key)} onCheckedChange={() => setForm({ ...form, permissions: togglePerm(form.permissions, m.key) })} />
                      {m.label}
                    </label>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Button variant="outline" size="sm" onClick={() => setForm({ ...form, permissions: PERMISSION_MODULES.map((m) => m.key) })} data-testid="new-perm-select-all">Select all</Button>
                  <Button variant="outline" size="sm" onClick={() => setForm({ ...form, permissions: [] })} data-testid="new-perm-clear-all">Clear all</Button>
                </div>
              </div>
            )}
            {form.role === "admin" && <p className="text-xs text-muted-foreground">Admins have full access to every module.</p>}
            {form.role === "investor" && <p className="text-xs text-muted-foreground">Investors are hard-limited to Bookings + Calendar for their linked car(s). No allowances needed.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={createUser} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-user-button">
              {saving ? "Saving…" : "Create user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!accessUser} onOpenChange={(o) => !o && setAccessUser(null)}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="edit-access-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Edit operator</DialogTitle>
            <DialogDescription>{accessUser?.email} — change anything about this login: details, account type, allowances, links and password.</DialogDescription>
          </DialogHeader>

          {accessForm && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Name"><Input value={accessForm.name} onChange={(e) => setAccessForm({ ...accessForm, name: e.target.value })} className="bg-input border-border" data-testid="edit-user-name" /></Field>
                <Field label="Email (their login)"><Input type="email" value={accessForm.email} onChange={(e) => setAccessForm({ ...accessForm, email: e.target.value })} className="bg-input border-border" data-testid="edit-user-email" /></Field>
              </div>

              <Field label="Account type">
                <Select value={accessRole} onValueChange={setAccessRole}>
                  <SelectTrigger className="bg-input border-border" data-testid="edit-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACCOUNT_TYPES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              {accessRole === "admin" && (
                <p className="text-xs text-amber-400" data-testid="edit-admin-warning">Making this an Admin gives full access to everything — and admin logins can no longer be edited from this screen.</p>
              )}
              {accessRole === "investor" && (
                <p className="text-xs text-muted-foreground">Investor logins are locked to their own car&apos;s bookings &amp; calendar — allowances are ignored.</p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Salesperson (My Bookings)">
                  <Select value={accessForm.salesperson} onValueChange={(v) => setAccessForm({ ...accessForm, salesperson: v })}>
                    <SelectTrigger className="bg-input border-border h-9" data-testid="edit-user-salesperson"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Not linked —</SelectItem>
                      {salespeople.map((sp) => <SelectItem key={sp.id} value={sp.name}>{sp.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Driver (My Schedule)">
                  <Select value={accessForm.chauffeur_id} onValueChange={(v) => setAccessForm({ ...accessForm, chauffeur_id: v })}>
                    <SelectTrigger className="bg-input border-border h-9" data-testid="edit-user-chauffeur"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Not linked —</SelectItem>
                      {chauffeurs.map((c) => <SelectItem key={c.id} value={c.id}>{`${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || c.id}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Car owner (Investor)">
                  <Select value={accessForm.investor_owner_id} onValueChange={(v) => setAccessForm({ ...accessForm, investor_owner_id: v })}>
                    <SelectTrigger className="bg-input border-border h-9" data-testid="edit-user-investor"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Not linked —</SelectItem>
                      {carOwners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div>
                <Label className="label-mono mb-1.5 block">Allowances — tick what this person can access</Label>
                <div className={`grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 border border-border rounded-md p-3 max-h-64 overflow-y-auto ${accessRole === "admin" || accessRole === "investor" ? "opacity-50 pointer-events-none" : ""}`}>
                  {PERMISSION_MODULES.map((m) => (
                    <label key={m.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`edit-perm-${m.key}`}>
                      <Checkbox checked={accessPerms.includes(m.key)} onCheckedChange={() => setAccessPerms((p) => togglePerm(p, m.key))} />
                      {m.label}
                    </label>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Button variant="outline" size="sm" onClick={() => setAccessPerms(PERMISSION_MODULES.map((m) => m.key))} data-testid="access-select-all">Select all</Button>
                  <Button variant="outline" size="sm" onClick={() => setAccessPerms([])} data-testid="access-clear-all">Clear all</Button>
                </div>
              </div>

              <div className="border border-border rounded-md p-4 space-y-3" data-testid="edit-password-block">
                <div className="flex items-center gap-2"><Lock size={14} className="text-primary" /><div className="label-mono">Reset to a temporary password</div></div>
                <p className="text-xs text-muted-foreground">Leave blank to keep their current password. Otherwise they sign in with this temp password and are asked to set their own straight away.</p>
                <PasswordInput placeholder="Temporary password (min 6 characters)" value={pwReset.password} onChange={(e) => setPwReset({ ...pwReset, password: e.target.value })} className="bg-input border-border" data-testid="edit-user-new-password" />
                {accessUser?.must_change_password && (
                  <p className="text-xs text-amber-400" data-testid="edit-must-change-flag">This login is on a temporary password and will be asked to set their own at next sign-in.</p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAccessUser(null)}>Cancel</Button>
            <Button onClick={saveAccess} disabled={accessSaving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-access-button">{accessSaving ? "Saving…" : "Save changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="label-mono">{label}</div>
      <div className={`mt-1.5 ${mono ? "font-mono text-xs" : "font-heading text-base"}`}>{value || "—"}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-mono mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
